import axios from "axios";
import React from "react";
import OtpInput from "react-otp-input";
import { useDispatch, useSelector } from "react-redux";
import { CheckMark, OnboardingHeader } from "../../components/onboarding/OnboardingBrand";
import { TwButton, TwInputGroup, TwLocationInput, TwMainLayout, TwPhoneInputGroup, TwTextarea } from "../../components/tailwind";
import FieldIcon from "../../components/tailwind/FieldIcons";
import { classNames, getRootDomain, isValidEmail } from "../../helpers";
import { useLocalStorage } from "../../hooks";
import { useToast } from "../../provider/ToastProvider";
import * as userActions from "../../redux/reducers/userReducer";
import { userState } from "../../redux/reducers/userReducer";
import axiosInstance from "../../utils/axiosInstance";
import { getUrlForUsers } from "../../utils/confighelpers";
import { checkClubLink, cleanClubLinkInput, ClubLinkBadge, suggestClubLink, useClubLinkCheck } from "../../utils/clubLink";
import { hasClubDetails, isSubscribedOwner, ONBOARDING_ROUTES, OWNER_BUSINESS_TYPE } from "../../utils/onboarding";
import { cleanHandleInput, linksFromHandles, SOCIAL_PLATFORMS } from "../../utils/socialLinks";
import BusinessProfileImgInput from "../settings/businessProfile/BusinessProfileImgInput";

const SUB_STEPS = ["Club profile", "Location & contact", "Verify email"];
/** Only the handle / page name is typed; saved as full links (utils/socialLinks) */
const SOCIALS = ["instagram", "facebook"];
const PHONE_RE = /^\+\d{1,4}\s?\d{6,14}$/;

export default function ClubDetails({ navigation }) {
  const dispatch = useDispatch();
  const { addErrorToast } = useToast();
  const [userToken] = useLocalStorage("userToken");
  const userReduxState = useSelector(userState);
  const onBoardingData = userReduxState?.onBoardingData || {};
  const [subStep, setSubStep] = React.useState(0);
  const [errors, setErrors] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [showSocials, setShowSocials] = React.useState(false);
  const [emailToken, setEmailToken] = React.useState("");
  const [otp, setOtp] = React.useState("");
  const [resendSeconds, setResendSeconds] = React.useState(0);
  /** Google / Apple sign-ups (2026-10-28, same onboarding as phone sign-ups): no verified phone yet, so they type the
   *  club phone here; their account email is already confirmed by Google / Apple, so that email needs no code. */
  const [account, setAccount] = React.useState({ social: false, email: "", hasPhone: true });
  const [phoneCountry, setPhoneCountry] = React.useState("nl");
  const [profile, setProfile] = React.useState({
    businessName: onBoardingData.clubName || "",
    businessTypes: OWNER_BUSINESS_TYPE,
    businessProfileType: "business",
    logo: "",
    images: [],
    coverPhoto: "",
    about: "",
    locationObj: {},
    address: {},
    typeOfBusiness: "",
    workNumber: "",
    workEmail: "",
    workWebsite: "",
    selectedView: OWNER_BUSINESS_TYPE,
    socialLink: {},
    socialLinksSelected: [],
  });

  // Club link (member address <link>.<root>): suggested from the club name until the owner edits it, checked while
  // typing, sent with the final save. It can't be changed later (only by FitClub support).
  const [clubLink, setClubLink] = React.useState(() => suggestClubLink(onBoardingData.clubName || ""));
  const [linkEdited, setLinkEdited] = React.useState(false);
  const linkCheck = useClubLinkCheck(clubLink);
  React.useEffect(() => {
    if (!linkEdited) setClubLink(suggestClubLink(profile.businessName));
  }, [profile.businessName, linkEdited]);

  const setField = React.useCallback((name, value) => {
    setProfile((prev) => ({ ...prev, [name]: value }));
  }, []);

  React.useEffect(() => {
    (async () => {
      const response = await axiosInstance({ method: "get", url: `${getUrlForUsers()}getUserInfo` });
      if (response?.data?.statusCode !== 200) return;
      const data = response.data.data;
      dispatch(userActions.setUserData(data));
      if (!isSubscribedOwner(data)) {
        navigation(ONBOARDING_ROUTES.plan, { replace: true });
        return;
      }
      if (hasClubDetails(data)) {
        navigation(ONBOARDING_ROUTES.payouts, { replace: true });
        return;
      }
      const social = !!(data.socialType && data.socialType !== "normal");
      const hasPhone = !!(data.countryCode && data.phoneNumber);
      setAccount({ social, email: String(data.email || (social ? data.username : "") || "").trim(), hasPhone });
      setProfile((prev) => ({
        ...prev,
        businessName: prev.businessName || data.fullName || "",
        workNumber: data.countryCode && data.phoneNumber ? `${data.countryCode} ${data.phoneNumber}` : prev.workNumber,
        workEmail: prev.workEmail || data.email || "",
      }));
      if (userReduxState.businessType !== OWNER_BUSINESS_TYPE) {
        dispatch(userActions.setBusinessType(OWNER_BUSINESS_TYPE));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (resendSeconds <= 0) return undefined;
    const tick = setTimeout(() => setResendSeconds((s) => s - 1), 1000);
    return () => clearTimeout(tick);
  }, [resendSeconds]);

  const sendEmailCode = async () => {
    const response = await axios({
      method: "put",
      url: `${getUrlForUsers()}updateProfile`,
      headers: { "x-access-token": userToken },
      data: { email: profile.workEmail.trim() },
    });
    if (response?.data?.statusCode !== 200 || !response?.data?.data?.token) {
      setErrors({ workEmail: response?.data?.message || "We couldn't send a code to this email." });
      return false;
    }
    setEmailToken(response.data.data.token);
    setResendSeconds(30);
    return true;
  };

  const next = async () => {
    const found = {};
    if (subStep === 0) {
      if (!profile.businessName.trim()) found.businessName = "Please enter your club name.";
      if (!profile.about || profile.about.trim().length < 5) found.about = "Add a short line about your club (at least 5 characters).";
      if (Object.keys(found).length === 0) {
        setSaving(true);
        const r = linkCheck.state === "ok" && linkCheck.link === clubLink.trim() ? linkCheck : await checkClubLink(clubLink);
        setSaving(false);
        if (r.state !== "ok") found.myBookingLink = r.message || "Please choose another club link.";
      }
    }
    if (subStep === 1) {
      if (!profile.address?.addressName && !profile.address?.address) found.address = "Please pick your club's address from the list.";
      if (!profile.workEmail) found.workEmail = "Please enter your business email.";
      else if (!isValidEmail(profile.workEmail.trim())) found.workEmail = "Please enter a valid email address.";
      if (!account.hasPhone && !PHONE_RE.test(String(profile.workNumber || "").trim())) found.workNumber = "Please enter your club's phone number.";
    }
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    if (subStep === 1 && emailAlreadyConfirmed) {
      await finish({ skipCode: true });
      return;
    }
    if (subStep === 1) {
      setSaving(true);
      const sent = await sendEmailCode();
      setSaving(false);
      if (!sent) return;
    }
    setSubStep((s) => s + 1);
  };

  const finish = async ({ skipCode = false } = {}) => {
    if ((!skipCode && otp.length < 4) || saving) return;
    setSaving(true);
    setErrors({});
    try {
      if (!skipCode) {
        const verify = await axios({
          method: "put",
          url: `${getUrlForUsers()}verifyPrimaryFieldsOtpAndUpdate`,
          headers: { "x-access-token": userToken },
          data: { code: otp, token: emailToken },
        });
        if (verify?.data?.statusCode !== 200) {
          setErrors({ otp: verify?.data?.message || "That code isn't right. Please try again." });
          setOtp("");
          setSaving(false);
          return;
        }
      }
      await axios({
        method: "post",
        url: `${getUrlForUsers()}addLocation`,
        headers: { "x-access-token": userToken },
        data: { ...profile.address },
      });
      const myBusinessProfile = {
        ...profile,
        businessName: profile.businessName.trim(),
        about: profile.about.trim(),
        workEmail: profile.workEmail.trim(),
        workNumber: String(profile.workNumber || "").trim(),
        socialLink: linksFromHandles(profile.socialLink || {}),
        locationObj: profile.address,
      };
      const save = await axios.put(
        `${getUrlForUsers()}updateProfile`,
        { businessType: OWNER_BUSINESS_TYPE, myBusinessProfile, myBookingLink: clubLink.trim(), onBoardingStep: 4 },
        { headers: { "x-access-token": userToken } }
      );
      if (save?.data?.statusCode !== 200) {
        const msg = typeof save?.data?.message === "string" ? save.data.message : "";
        if (/link/i.test(msg)) {
          // Someone took the link in the meantime: back to step 1 to pick another (the email is verified already).
          setSubStep(0);
          setLinkEdited(true);
          setErrors({ myBookingLink: msg });
          setOtp("");
          setSaving(false);
          return;
        }
        throw new Error(msg || "We couldn't save your club details.");
      }
      dispatch(userActions.setUserData(save.data.data));
      dispatch(userActions.setOnBoardingData(save.data.data));
      dispatch(userActions.setBusinessType(OWNER_BUSINESS_TYPE));
      navigation(ONBOARDING_ROUTES.payouts, { replace: true });
    } catch (error) {
      addErrorToast(error?.message || "Something went wrong. Please try again.");
      setSaving(false);
    }
  };

  const emailAlreadyConfirmed =
    account.social && !!account.email && String(profile.workEmail || "").trim().toLowerCase() === account.email.toLowerCase();

  React.useEffect(() => {
    if (subStep === 2 && otp.length === 4) finish();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  const addressLine = profile.address?.address || [profile.address?.street, profile.address?.city].filter(Boolean).join(", ") || "";

  return (
    <TwMainLayout fullScreen={true}>
      <div className="min-h-screen bg-[#F7F4F8]">
        <OnboardingHeader current={3} />

        <main className="mx-auto grid max-w-6xl grid-cols-1 gap-6 px-4 pb-16 pt-6 sm:px-8 lg:grid-cols-[minmax(0,1fr)_360px] lg:gap-8 lg:pt-10">
          <section className="rounded-3xl border border-gray-200 bg-white p-5 sm:p-8 shadow-[0_20px_50px_rgba(72,27,58,0.08)]">
            <ol className="flex flex-wrap items-center gap-2 text-xs sm:text-sm font-bold" aria-label="Club details progress">
              {SUB_STEPS.map((label, i) => (
                <li key={label} className="flex items-center gap-2">
                  {i > 0 && <span className="h-px w-5 sm:w-8 bg-gray-300" aria-hidden="true" />}
                  <span className={classNames("flex items-center gap-1.5", i === subStep ? "text-indigo-bz" : i < subStep ? "text-gray-700" : "text-gray-400")}>
                    <span className={classNames("flex h-5 w-5 items-center justify-center rounded-full text-[10px]", i < subStep ? "bg-bz-status-green text-white" : i === subStep ? "bg-indigo-bz text-white" : "border-2 border-gray-300")}>
                      {i < subStep ? <CheckMark className="h-3 w-3" /> : i + 1}
                    </span>
                    {label}
                  </span>
                </li>
              ))}
            </ol>

            {subStep === 0 && (
              <div className="mt-6 flex flex-col gap-5">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-gray-900">Make your club look great</h1>
                  <p className="mt-1.5 text-sm sm:text-base text-gray-600">This is the first thing members see on your club page and in the app.</p>
                </div>
                <BusinessProfileImgInput setFieldValue={setField} values={profile} errors={errors} />
                <TwInputGroup
                  label="Club name"
                  name="businessName"
                  placeholder="e.g. Studio Noord"
                  leftIcon={<FieldIcon name="club" />}
                  wrapperClassName="w-full"
                  value={profile.businessName}
                  onChange={(e) => setField("businessName", e.target.value)}
                  errors={errors}
                />
                <TwInputGroup
                  label="Club link"
                  name="myBookingLink"
                  placeholder="yourclub"
                  leftIcon={<FieldIcon name="website" />}
                  rightIcon={
                    <span className="flex items-center gap-2 pr-1">
                      <span className="text-sm text-gray-500">.{getRootDomain()}</span>
                      <ClubLinkBadge state={linkCheck.state} />
                    </span>
                  }
                  wrapperClassName="w-full"
                  value={clubLink}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  onChange={(e) => {
                    setLinkEdited(true);
                    setClubLink(cleanClubLinkInput(e.target.value));
                    setErrors((x) => ({ ...x, myBookingLink: "" }));
                  }}
                  errors={
                    errors.myBookingLink
                      ? errors
                      : linkCheck.state === "invalid" || linkCheck.state === "taken"
                      ? { myBookingLink: linkCheck.message }
                      : {}
                  }
                  helperText={
                    errors.myBookingLink || linkCheck.state === "invalid" || linkCheck.state === "taken"
                      ? ""
                      : "Your members' web address for signing up and booking. Choose carefully: you can't change it yourself later."
                  }
                />
                <TwTextarea
                  wrapperClassName="w-full"
                  label="About your club"
                  name="about"
                  placeholder="e.g. Small-group yoga and HIIT in the heart of Amsterdam Noord."
                  defaultValue={profile.about}
                  onChange={(e) => setField("about", e.target.value)}
                  errors={errors}
                />
              </div>
            )}

            {subStep === 1 && (
              <div className="mt-6 flex flex-col gap-5">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-gray-900">Where can members find you?</h1>
                  <p className="mt-1.5 text-sm sm:text-base text-gray-600">Your address shows on class pages and in maps. Your email is used for bookings and billing.</p>
                </div>
                <TwLocationInput
                  label="Club address"
                  name="address"
                  leftIcon={<FieldIcon name="location" />}
                  wrapperClassName="w-full"
                  defaultLocationSelect={profile.address}
                  onLocationSelect={(data) => {
                    /* same as the legacy wizard: the location is named after the club */
                    const addressObj = { ...data, addressName: profile.businessName || data?.addressName };
                    setField("address", addressObj);
                    setField("locationObj", addressObj);
                  }}
                  errors={errors}
                />
                <TwInputGroup
                  label="Business email"
                  name="workEmail"
                  type="email"
                  placeholder="hello@yourclub.nl"
                  leftIcon={<FieldIcon name="email" />}
                  wrapperClassName="w-full"
                  value={profile.workEmail}
                  onChange={(e) => setField("workEmail", e.target.value)}
                  errors={errors}
                />
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  {account.hasPhone ? (
                    <TwInputGroup label="Phone" name="workNumber" value={profile.workNumber} disabled onChange={() => {}} leftIcon={<FieldIcon name="phone" />} wrapperClassName="w-full" />
                  ) : (
                    <TwPhoneInputGroup
                      label="Club phone"
                      name="workNumber"
                      placeholder="6 12345678"
                      country={phoneCountry}
                      errors={errors}
                      onChange={(phone, country) => {
                        const dial = String(country?.dialCode || "");
                        const national = String(phone || "").replace(/\D/g, "").slice(dial.length).replace(/^0+/, "");
                        setPhoneCountry(country?.countryCode || phoneCountry);
                        setField("workNumber", national ? `+${dial} ${national}` : "");
                        setErrors((x) => ({ ...x, workNumber: "" }));
                      }}
                    />
                  )}
                  <TwInputGroup
                    label="Website (optional)"
                    name="workWebsite"
                    placeholder="www.yourclub.nl"
                    leftIcon={<FieldIcon name="website" />}
                    wrapperClassName="w-full"
                    value={profile.workWebsite}
                    onChange={(e) => setField("workWebsite", e.target.value)}
                  />
                </div>
                {!showSocials ? (
                  <button type="button" onClick={() => setShowSocials(true)} className="self-start text-sm font-extrabold text-indigo-bz hover:text-indigo-bz-hover">
                    + Add Instagram or Facebook (optional)
                  </button>
                ) : (
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    {SOCIALS.map((key) => (
                      <TwInputGroup
                        key={key}
                        label={SOCIAL_PLATFORMS[key].label}
                        name={`social-${key}`}
                        aria-label={`${SOCIAL_PLATFORMS[key].label} handle or page name`}
                        leftIcon={
                          <span className="flex items-center gap-2">
                            <FieldIcon name={key} />
                            <span className="text-gray-400">{SOCIAL_PLATFORMS[key].prefix}</span>
                          </span>
                        }
                        wrapperClassName="w-full"
                        className="!pl-0.5 md:!pl-0.5"
                        placeholder="yourclub"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        value={profile.socialLink?.[key] || ""}
                        onChange={(e) => {
                          const value = cleanHandleInput(key, e.target.value);
                          setProfile((prev) => {
                            const socialLink = { ...(prev.socialLink || {}), [key]: value };
                            const socialLinksSelected = Object.keys(socialLink).filter((k) => socialLink[k]);
                            return { ...prev, socialLink, socialLinksSelected };
                          });
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {subStep === 2 && (
              <div className="mt-6 flex flex-col gap-4">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-gray-900">Check your inbox</h1>
                  <p className="mt-1.5 text-sm sm:text-base text-gray-600">
                    We sent a 4-digit code to <span className="font-bold text-gray-900">{profile.workEmail}</span>.{" "}
                    <button type="button" className="font-bold text-indigo-bz underline" onClick={() => { setSubStep(1); setOtp(""); setErrors({}); }}>Change email</button>
                  </p>
                </div>
                <OtpInput
                  value={otp}
                  onChange={setOtp}
                  numInputs={4}
                  containerStyle="flex gap-3 sm:gap-4"
                  inputStyle={classNames(
                    "!w-14 !h-16 sm:!w-16 sm:!h-[72px] rounded-2xl border-[1.5px] bg-[#FAF8FB] text-2xl sm:text-3xl font-black text-gray-900 outline-none transition focus:border-indigo-bz focus:ring-4 focus:ring-indigo-bz/15",
                    errors.otp ? "border-red-400" : "border-gray-300"
                  )}
                  renderInput={(props, index) => <input {...props} aria-label={`Digit ${index + 1}`} autoComplete={index === 0 ? "one-time-code" : "off"} />}
                  shouldAutoFocus
                  inputType="tel"
                />
                {errors.otp && <p className="text-sm font-semibold text-red-600" role="alert">{errors.otp}</p>}
                <p className="text-sm text-gray-600">
                  Didn't get it? Check your spam folder or{" "}
                  {resendSeconds > 0 ? (
                    <span className="font-semibold text-gray-500">resend in 0:{String(resendSeconds).padStart(2, "0")}</span>
                  ) : (
                    <button type="button" className="font-bold text-indigo-bz underline" onClick={sendEmailCode}>resend the code</button>
                  )}
                  .
                </p>
              </div>
            )}

            <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
              {subStep > 0 ? (
                <button type="button" onClick={() => { setSubStep((s) => s - 1); setErrors({}); setOtp(""); }} className="text-sm font-bold text-gray-600 hover:text-gray-900">
                  ← Back
                </button>
              ) : <span />}
              <TwButton
                onClick={subStep === 2 ? finish : next}
                disabled={saving || (subStep === 2 && otp.length < 4)}
                actionLoading={saving}
                className="w-full sm:w-auto sm:min-w-56 !rounded-2xl !bg-brown-coffee py-3.5 text-base font-extrabold hover:!bg-light-brown-coffee"
              >
                {subStep === 0 ? "Continue" : subStep === 1 ? (emailAlreadyConfirmed ? "Save club" : "Continue & verify email") : "Verify & save club"}
              </TwButton>
            </div>
          </section>

          <aside className="hidden lg:block">
            <div className="lg:sticky lg:top-24">
              <p className="mb-2 text-xs font-extrabold tracking-wide text-gray-500">PREVIEW · WHAT MEMBERS SEE</p>
              <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-[0_20px_50px_rgba(72,27,58,0.08)]">
                <div className="relative h-28 bg-brown-coffee">
                  {profile.coverPhoto && <img src={profile.coverPhoto} alt="" className="h-full w-full object-cover" />}
                  <div className="absolute -bottom-8 left-5 flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl border-4 border-white bg-[#F3E9FF] text-xl font-black text-indigo-bz">
                    {profile.logo ? <img src={profile.logo} alt="" className="h-full w-full object-cover" /> : (profile.businessName || "C").slice(0, 1).toUpperCase()}
                  </div>
                </div>
                <div className="px-5 pb-5 pt-11">
                  <p className="text-lg font-black text-gray-900">{profile.businessName || "Your club"}</p>
                  <p className="mt-0.5 truncate text-xs font-bold text-indigo-bz">{(clubLink || "yourclub") + "." + getRootDomain()}</p>
                  <p className="mt-1 line-clamp-3 text-sm text-gray-600">{profile.about || "A short description of your club appears here."}</p>
                  <div className="mt-4 flex flex-col gap-1.5 text-sm text-gray-700">
                    <span className="flex items-center gap-2">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 text-portland-orange" aria-hidden="true"><path d="M12 21s-7-6.2-7-11a7 7 0 1 1 14 0c0 4.8-7 11-7 11z" /><circle cx="12" cy="10" r="2.5" /></svg>
                      {addressLine || "Address"}
                    </span>
                    {profile.workWebsite && (
                      <span className="flex items-center gap-2">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 text-portland-orange" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" /></svg>
                        {profile.workWebsite}
                      </span>
                    )}
                  </div>
                  <div className="mt-4 rounded-2xl bg-[#F7F4F8] p-3">
                    <div className="flex items-center justify-between">
                      <span>
                        <span className="block text-sm font-extrabold text-gray-900">Your first class</span>
                        <span className="block text-xs text-gray-500">You'll add this next</span>
                      </span>
                      <span className="rounded-lg bg-brown-coffee px-3 py-1.5 text-xs font-bold text-white">Book</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </main>
      </div>
    </TwMainLayout>
  );
}
